import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, subirArchivo } from "@/lib/api";
import { Spinner } from "@/components/common";
import { UploadCloud, X, Replace } from "lucide-react";

export const avisarDocs = () => window.dispatchEvent(new Event("docs-alertas"));

function CargaModal({ eid, reemplazo, beneficiarios, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ categoria: reemplazo?.categoria || "", observacion: "", beneficiario_id: reemplazo?.beneficiario_id || "", aprobar: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  useEffect(() => { api.get("/meta/hoja-vida").then((r) => setCats(r.data.categorias_documento || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const enviar = async () => {
    if (!f.categoria || !file) { toast.error("Selecciona la categoría y el archivo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("categoria", f.categoria); fd.append("observacion", f.observacion); fd.append("aprobar", f.aprobar ? "true" : "false"); fd.append("file", file);
      if (f.beneficiario_id) fd.append("beneficiario_id", f.beneficiario_id);
      if (reemplazo) fd.append("reemplazar_doc_id", reemplazo.id);
      await subirArchivo(`/expedientes/${eid}/documentos`, fd);
      toast.success(reemplazo ? "Documento reemplazado. La versión anterior queda en el historial." : "Documento cargado al expediente");
      avisarDocs(); onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inp = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-vino-500";
  const opciones = cats.includes(f.categoria) || !f.categoria ? cats : [f.categoria, ...cats];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" data-testid="cargar-doc-modal">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-semibold text-slate-800">{reemplazo ? "Reemplazar documento" : "Cargar documento"}</p>
          <button onClick={onClose} data-testid="cargar-doc-cerrar" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {reemplazo && <p className="mb-3 text-xs text-slate-500">Actual: {reemplazo.categoria} · {reemplazo.original_filename} (v{reemplazo.version || 1}). La versión actual se conserva en el historial.</p>}
        <div className="space-y-3">
          <select value={f.categoria} onChange={(e) => set("categoria", e.target.value)} data-testid="cargar-doc-categoria" className={inp}>
            <option value="">Categoría del documento</option>
            {opciones.map((c) => <option key={c}>{c}</option>)}
          </select>
          {!reemplazo && beneficiarios.length > 0 && (
            <select value={f.beneficiario_id} onChange={(e) => set("beneficiario_id", e.target.value)} data-testid="cargar-doc-beneficiario" className={inp}>
              <option value="">Documento del trabajador</option>
              {beneficiarios.map((b) => <option key={b.id} value={b.id}>Soporte de beneficiario: {b.nombre}</option>)}
            </select>
          )}
          <button type="button" onClick={() => ref.current?.click()} data-testid="cargar-doc-seleccionar" className="flex w-full items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:border-vino-400">
            <UploadCloud className="h-4 w-4" /> <span className="truncate">{file ? file.name : "Seleccionar archivo (PDF, JPG o PNG, máx. 10 MB)"}</span>
          </button>
          <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="cargar-doc-input" onChange={(e) => setFile(e.target.files[0])} />
          <textarea value={f.observacion} onChange={(e) => set("observacion", e.target.value)} rows={2} placeholder="Observaciones (opcional)" data-testid="cargar-doc-observacion" className={inp} />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.aprobar} onChange={(e) => set("aprobar", e.target.checked)} data-testid="cargar-doc-aprobar" className="accent-vino-700" />
            Dejarlo aprobado (validado por HUMAN)
          </label>
        </div>
        <button onClick={enviar} disabled={busy} data-testid="cargar-doc-guardar" className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
          {busy ? <Spinner /> : <UploadCloud className="h-4 w-4" />} {reemplazo ? "Reemplazar" : "Cargar al expediente"}
        </button>
      </div>
    </div>
  );
}

export function CargarDocumentoBtn({ eid, beneficiarios = [], onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="btn-cargar-documento" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800">
        <UploadCloud className="h-3.5 w-3.5" /> Cargar documento
      </button>
      {open && <CargaModal eid={eid} beneficiarios={beneficiarios.filter((b) => b.id)} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

export function ReemplazarDocBtn({ eid, doc, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`reemplazar-doc-${doc.id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
        <Replace className="h-3.5 w-3.5" /> Reemplazar
      </button>
      {open && <CargaModal eid={eid} reemplazo={doc} beneficiarios={[]} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}
